import React, { useState, useEffect, useRef } from "react";
import { ReportLog } from "../../Models";
import { ChevronDownIcon, ChevronUpIcon, EllipsisVerticalIcon } from "@heroicons/react/20/solid";

interface IssueRowProps {
  issue: ReportLog;
  onDelete: (id: string) => void;
  onStatusChange: (newStatus: "open" | "closed") => void;
  selectedIssues: string[];
  onSelect: (id: string) => void;
  onShowInfo: (title: string, description: string) => void;
  isLast?: boolean;
}

function IssueRow({ issue, onDelete, onStatusChange, selectedIssues, onSelect, onShowInfo, isLast }: IssueRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const statusMenuRef = useRef<HTMLTableDataCellElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
      if (statusMenuOpen && statusMenuRef.current && !statusMenuRef.current.contains(e.target as Node)) {
        setStatusMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen, statusMenuOpen]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString.endsWith("Z") ? dateString : dateString + "Z");
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const truncateText = (text: string, maxLength: number = 150) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const parseUserAgent = (userAgent: string) => {
    // Browser name + version
    let browser = "Unknown";
    if (userAgent.includes("Edg/")) {
      const match = userAgent.match(/Edg\/([\d.]+)/);
      browser = "Edge" + (match ? " " + match[1] : "");
    } else if (userAgent.includes("OPR/") || userAgent.includes("Opera/")) {
      const match = userAgent.match(/(?:OPR|Opera)\/([\d.]+)/);
      browser = "Opera" + (match ? " " + match[1] : "");
    } else if (userAgent.includes("Chrome/") && !userAgent.includes("Edg")) {
      const match = userAgent.match(/Chrome\/([\d.]+)/);
      browser = "Chrome" + (match ? " " + match[1].split(".")[0] : "");
    } else if (userAgent.includes("Firefox/")) {
      const match = userAgent.match(/Firefox\/([\d.]+)/);
      browser = "Firefox" + (match ? " " + match[1] : "");
    } else if (userAgent.includes("Safari/") && !userAgent.includes("Chrome")) {
      const match = userAgent.match(/Version\/([\d.]+)/);
      browser = "Safari" + (match ? " " + match[1] : "");
    }

    // OS
    let os = "Unknown OS";
    if (userAgent.includes("Windows NT 10.0")) {
      os = "Windows 10" + (userAgent.includes("Win64; x64") ? " (64-bit)" : "");
    } else if (userAgent.includes("Windows NT 11.0") || (userAgent.includes("Windows NT 10.0") && userAgent.includes("Windows 11"))) {
      os = "Windows 11" + (userAgent.includes("Win64; x64") ? " (64-bit)" : "");
    } else if (userAgent.includes("Mac OS X")) {
      const match = userAgent.match(/Mac OS X ([\d_]+)/);
      os = "macOS" + (match ? " " + match[1].replace(/_/g, ".") : "");
    } else if (userAgent.includes("Linux")) {
      os = "Linux";
    } else if (userAgent.includes("Android")) {
      const match = userAgent.match(/Android ([\d.]+)/);
      os = "Android" + (match ? " " + match[1] : "");
    } else if (userAgent.includes("iPhone OS") || userAgent.includes("iPad")) {
      const match = userAgent.match(/OS ([\d_]+)/);
      os = "iOS" + (match ? " " + match[1].replace(/_/g, ".") : "");
    }

    // Device type
    let device = "Desktop";
    if (/Mobile|Android.*Mobile|iPhone/.test(userAgent)) {
      device = "Mobile";
    } else if (/iPad|Android(?!.*Mobile)|Tablet/.test(userAgent)) {
      device = "Tablet";
    }

    return { browser, os, device };
  };

  const getBrowserName = (userAgent: string) => {
    return parseUserAgent(userAgent).browser.split(" ")[0];
  };

  const getCategoryColor = (title: string) => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes("technical")) return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
    if (lowerTitle.includes("form")) return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
    if (lowerTitle.includes("submission")) return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
    return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";
  };

  const getStatusColor = (status: string) => {
    const lowerStatus = status.toLowerCase();
    if (lowerStatus === "open") return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
    if (lowerStatus === "closed") return "bg-gray-200 text-gray-800 dark:bg-gray-800 dark:text-gray-200";
    return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200";
  };

  return (
    <>
      <tr className="hover:bg-gray-50 dark:hover:bg-[#1c2229] transition-colors">
        <td className="py-4 pl-4 pr-2 whitespace-nowrap text-sm">
          <input
            type="checkbox"
            checked={selectedIssues.includes(issue.id)}
            onChange={() => onSelect(issue.id)}
            className="form-checkbox h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
          />
        </td>
        <td className="py-4 pl-6 pr-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
          {formatDate(issue.timestamp)}
        </td>
        <td className="px-3 py-4 whitespace-nowrap text-sm">
          <a
            href={`mailto:${issue.email}`}
            className="text-entntblue hover:text-entntorange dark:text-blue-400 dark:hover:text-blue-300"
          >
            {issue.email}
          </a>
        </td>
        <td className="px-3 py-4 whitespace-nowrap text-sm">
          <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getCategoryColor(issue.title)}`}>
            {issue.title.split(" ").slice(0, -1).join(" ") || issue.title}
          </span>
        </td>
        <td className="px-3 py-4 text-sm text-gray-900 dark:text-gray-100 max-w-[200px]">
          {issue.role ? (
            <span className="text-xs font-medium">
              {issue.role.charAt(0).toUpperCase() + issue.role.slice(1)}
            </span>
          ) : (
            <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
          )}
        </td>
        <td className="px-3 py-4 whitespace-nowrap text-sm relative" ref={statusMenuRef}>
          <button
            type="button"
            className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${getStatusColor(issue.status)}`}
            onClick={() => setStatusMenuOpen((prev) => !prev)}
          >
            {issue.status.charAt(0).toUpperCase() + issue.status.slice(1)}
            <ChevronDownIcon className="h-3 w-3 ml-1" />
          </button>
          {statusMenuOpen && (
            <div className="absolute z-10 mt-1 w-24 rounded-md bg-white dark:bg-darkbglight shadow-lg ring-1 ring-black ring-opacity-5">
              <button
                type="button"
                className="block w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                onClick={() => {
                  const current = issue.status.toLowerCase();
                  const nextStatus = current === "open" ? "closed" : "open";
                  onStatusChange(nextStatus as "open" | "closed");
                  setStatusMenuOpen(false);
                }}
              >
                {issue.status.toLowerCase() === "open" ? "Closed" : "Open"}
              </button>
            </div>
          )}
        </td>
        <td className="px-3 py-4 text-sm text-gray-900 dark:text-gray-100">
          <div>
            {expanded ? issue.description : truncateText(issue.description)}
            {issue.description.length > 150 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="ml-2 text-entntblue hover:text-entntorange dark:text-blue-400 inline-flex items-center"
              >
                {expanded ? (
                  <>
                    <span className="text-xs">Show less</span>
                    <ChevronUpIcon className="h-4 w-4 ml-1" />
                  </>
                ) : (
                  <>
                    <span className="text-xs">Show more</span>
                    <ChevronDownIcon className="h-4 w-4 ml-1" />
                  </>
                )}
              </button>
            )}
          </div>
        </td>
        <td className="py-4 pr-6 pl-3 text-right text-sm text-gray-500 dark:text-gray-400">
          <div className="relative inline-block text-left" ref={menuRef}>
            <button
              type="button"
              className="inline-flex justify-center w-full px-2 py-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              aria-haspopup="true"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((prev) => !prev)}
            >
              <EllipsisVerticalIcon className="h-5 w-5" />
            </button>
            {menuOpen && (
              <div className={`absolute right-0 w-44 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-darkbglight z-10 ${isLast ? "origin-bottom-right bottom-full mb-2" : "origin-top-right mt-2"}`}>
                <button
                  onClick={() => {
                    const info = parseUserAgent(issue.userAgent);
                    onShowInfo("User Agent Info", `Browser: ${info.browser}\nOS: ${info.os}\nDevice: ${info.device}`);
                    setMenuOpen(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  <span className="inline-flex items-center rounded-md bg-gray-100 dark:bg-gray-700 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 mr-2">
                    {getBrowserName(issue.userAgent)}
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-4 h-4 mr-1"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                    />
                  </svg>
                  {/* <span>View user agent</span> */}
                </button>
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    onDelete(issue.id);
                  }}
                  className="block w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        </td>
      </tr>
    </>
  );
}

export default IssueRow;